import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { GetBillTosApiParameter, GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import loadShipTos from "@tLinc/Handlers/CustomLoadShipTos";
// import { CustomActions, CustomState } from "@tLinc/Store/Reducer/Reducers";
import CustomApplicationState from "@tLinc/Store/Data/CustomApplicationState/CustomApplicationState";
import { CustomActions } from "@tLinc/Store/Reducer/CustomReducer/CustomerReducer";
import React, { FC, useEffect, useState } from "react";
import { connect, DispatchProp, ResolveThunks } from "react-redux";

interface OwnProps {
    extendedStyles?: ChangeCustomerShipToSelectorStyles;
    setParameter: (parameter: GetShipTosApiParameter) => void;
    parameter: GetShipTosApiParameter;
    searchText: string;
    setSearchText: (searchText: string) => void;
    enableWarehousePickup: boolean;
    fulfillmentMethod: string;
    shipTo?: ShipToModel;
    billToId?: string;
    allowSelectBillTo?: boolean;
    onSelect: (shipTo: ShipToModel) => void;
    onCreateNewAddressClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    setBillTosAndShipTos: (billToId: string, shipToId: string) => void;
    billTosParameter: GetBillTosApiParameter;
    setBillTosParameter: (parameter: GetBillTosApiParameter) => void;
    isLoading?: boolean;
}

const mapStateToProps = (state: CustomApplicationState, props: OwnProps) => {
    const shipTosArray = Object.values(state.data.shipTos.byId);
    const shipTosArrayFiltered = state.data.shipTos.byId;
    const dataViews = Object.values(state.data.shipTos.dataViews);
    // const shipTosIds = dataViews[dataViews.length - 1] && dataViews[dataViews.length - 1].ids;
    const shipTosIds = dataViews[1] && dataViews[1].ids;
    const newArr: any[] = [];
    shipTosIds?.forEach(item => newArr.push(shipTosArrayFiltered[item]));

    return {
        shipTosDataView: getShipTosDataView(state, props.parameter),
        shipTosArray: state.LINCData.shipTosReducer?.shipTosResult,
    };
};

const mapDispatchToProps = {
    loadShipTos,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ChangeCustomerShipToSelectorStyles {
    shipToSelectorDynamicDropdown?: DynamicDropdownPresentationProps;
}

export const changeCustomerShipToSelectorStyles: ChangeCustomerShipToSelectorStyles = {};

const ChangeCustomerShipToSelector: FC<Props> = ({
    extendedStyles,
    shipTo,
    billToId,
    shipTosDataView,
    onSelect,
    parameter,
    setParameter,
    searchText,
    setSearchText,
    enableWarehousePickup,
    fulfillmentMethod,
    isLoading,
    setBillTosAndShipTos,
    loadShipTos,
    billTosParameter,
    setBillTosParameter,
    shipTosArray,
}) => {
    const [styles] = useState(() => mergeToNew(changeCustomerShipToSelectorStyles, extendedStyles));
    const [options, setOptions] = useState<OptionObject[]>([]);
    let dropdownLabel = translate("Select Ship To");
    let dropdownPlaceholder = translate("Search or Select Ship To");

    if (enableWarehousePickup && fulfillmentMethod === FulfillmentMethod.PickUp) {
        dropdownLabel = translate("Recipient Address");
        dropdownPlaceholder = translate("Search or Select Recipient Address");
    }

    useEffect(() => {
        setSearchText("");
        setParameter({ ...parameter, billToId, filter: undefined });
    }, [billToId]);

    useEffect(() => {
        if (shipTosArray?.shipTos) {
            const options: OptionObject[] = shipTosArray.shipTos.map((shipTo: { label: string; id: any }) => {
                let { label } = shipTo;
                if (label === "Use Billing Address") {
                    return {
                        optionText: label,
                        optionValue: shipTo.id,
                    };
                }
                const customerNumber = label.split(" ")[0];
                while (label.charAt(0) !== " ") {
                    label = label.slice(1);
                }
                label = label.concat(` : ${customerNumber}`);
                return {
                    optionText: label,
                    optionValue: shipTo.id,
                };
            });

            const sortedOptions = options.slice().sort((a, b) => a.optionText.localeCompare(b.optionText));
            setOptions(sortedOptions);
            if (!shipTo && shipTosArray.shipTos.length === 1) {
                onSelect(shipTosArray.shipTos[0]);
            }
        }
    }, [shipTosArray]);

    const selectCustomerHandler = (shipToId?: string) => {
        const shipTo = shipTosArray.shipTos.filter((shipTo: { id: string | undefined }) => shipTo.id === shipToId)[0];
        if (shipTo) {
            onSelect(shipTo as ShipToModel);
            const parts = shipTo.uri.split("/"); // Split the string by "/"
            const desiredPart = parts[parts.indexOf("billtos") + 1]; // Get the part after "billtos"
            setBillTosAndShipTos(desiredPart, shipTo.id);
        }
    };

    const searchTextChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setBillTosParameter({ ...billTosParameter, filter: "" });
        setSearchText(event.currentTarget.value);
    };

    return (
        <DynamicDropdown
            {...styles.shipToSelectorDynamicDropdown}
            label={dropdownLabel}
            onSelectionChange={selectCustomerHandler}
            onInputChange={searchTextChanged}
            selected={shipTo?.id}
            placeholder={dropdownPlaceholder}
            isLoading={isLoading}
            options={options}
            search={searchText}
            data-test-selector="changeCustomerShipToSelector"
            filterOption={() => true}
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeCustomerShipToSelector);
